import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button } from 'reactstrap';
import { GlobalContext } from '../../api/GlobalContext';
import { PROFESSION_OPTIONS } from '../../constants/EmrNoteTypeMapping';

const MAX_RULES = 5;
const AUTOSAVE_DELAY_MS = 800;
const ALL_PROFESSIONS_KEY = 'all';
const PROFESSIONS = [
  { key: ALL_PROFESSIONS_KEY, label: 'All Professions' },
  ...PROFESSION_OPTIONS.map((p) => ({ key: p, label: p })),
];

const CATEGORIES = [
  {
    key: 'formatting_structure',
    label: 'Formatting & Structure',
    tooltip: 'How the note should look',
    placeholder: 'Always use the degrees symbol when documenting range of motion. Do not write out degrees.',
  },
  {
    key: 'content_requirements',
    label: 'Content Requirements',
    tooltip: 'What should / should NOT be included (generally)',
    placeholder: "Always include a brief description of the patient's response to treatment.",
  },
  {
    key: 'terminology_abbreviations',
    label: 'Terminology & Abbreviations',
    tooltip: 'How things should be worded',
    placeholder: "Write 'pt' instead of 'patient'",
  },
  {
    key: 'clinical_reasoning',
    label: 'Clinical Reasoning',
    tooltip: 'How the AI should think',
    placeholder: 'Avoid unnecessary words or filler language.',
  },
  {
    key: 'company_preferences',
    label: 'Company Preferences',
    tooltip: 'Clinic-specific rules and behaviors',
    placeholder: 'Document pediatric patients using age-appropriate terminology.',
  },
];

const TooltipIcon = ({ text }) => {
  const [visible, setVisible] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-block', marginLeft: '6px' }}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          border: '1px solid #888',
          fontSize: '11px',
          color: '#888',
          cursor: 'default',
          userSelect: 'none',
        }}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
      >
        ?
      </span>
      {visible && (
        <span
          style={{
            position: 'absolute',
            bottom: '22px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#333',
            color: '#fff',
            padding: '5px 10px',
            borderRadius: '4px',
            fontSize: '12px',
            whiteSpace: 'nowrap',
            zIndex: 9999,
            pointerEvents: 'none',
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
};

const RuleInput = ({ rule, index, onChange, onRemove, placeholder, autoFocus, onSave, saveText }) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [autoFocus]);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        marginBottom: '8px',
      }}
    >
      <span
        style={{
          minWidth: '20px',
          fontWeight: '600',
          color: '#555',
          paddingTop: '10px',
          fontSize: '13px',
        }}
      >
        {index + 1}.
      </span>
      <div
        style={{
          flex: 1,
          position: 'relative',
          border: '2px solid #6bd888',
          borderRadius: '8px',
        }}
      >
        <textarea
          ref={textareaRef}
          value={rule}
          onChange={(e) => onChange(index, e.target.value)}
          placeholder={placeholder}
          rows={2}
          style={{
            width: '100%',
            resize: 'none',
            border: 'none',
            borderRadius: '8px',
            padding: '10px',
            paddingBottom: '24px',
            fontSize: '14px',
            lineHeight: '1.4',
            outline: 'none',
            background: 'transparent',
          }}
        />
        <button
          onClick={onSave}
          disabled={saveText === 'Saving...'}
          style={{
            position: 'absolute',
            bottom: '6px',
            right: '8px',
            fontSize: '12px',
            color: '#888',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: saveText === 'Saving...' ? 'default' : 'pointer',
            padding: 0,
            lineHeight: 1,
          }}
        >
          {saveText}
        </button>
      </div>
      <button
        onClick={() => onRemove(index)}
        style={{
          background: 'none',
          border: 'none',
          color: '#999',
          cursor: 'pointer',
          padding: '4px',
          paddingTop: '10px',
          lineHeight: 1,
        }}
        title="Remove rule"
      >
        <i className="fa fa-trash" style={{ fontSize: '14px' }} />
      </button>
    </div>
  );
};

const CONTEXT_CONFIG = {
  admin: {
    title: 'Clinic-wide Note Settings',
    description: 'These are general rules applied globally to all notes for your organization.',
  },
  user: {
    title: 'Note Rules',
    description: 'These rules are applied to your notes when generating documentation.',
  },
};

const STATUS_LABEL = {
  saving: 'Saving...',
  saved: 'Saved',
  error: 'Failed to save',
};

const emptyRules = () => Object.fromEntries(CATEGORIES.map((c) => [c.key, []]));

const CompanyNoteSettings = ({ context = 'admin' }) => {
  const { apiClient } = useContext(GlobalContext);
  const [selectedProfession, setSelectedProfession] = useState(null);
  const [rules, setRules] = useState(emptyRules);
  const [focusTarget, setFocusTarget] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle');

  const { title, description } = CONTEXT_CONFIG[context] || CONTEXT_CONFIG.admin;

  // Initialize selectedProfession: admin defaults to their own profession, user gets first option
  useEffect(() => {
    if (context === 'admin') {
      apiClient.getStats().then((data) => {
        const userProfession = data?.profession;
        const isValid = PROFESSION_OPTIONS.includes(userProfession);
        setSelectedProfession(isValid ? userProfession : PROFESSION_OPTIONS[0] ?? '');
      }).catch(() => {
        setSelectedProfession(PROFESSION_OPTIONS[0] ?? '');
      });
    } else {
      setSelectedProfession(PROFESSION_OPTIONS[0] ?? '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context]);

  const getEndpoint = context === 'admin'
    ? '/v1/admin/companyNoteSettings/get'
    : '/v1/user/noteSettings/get';
  const saveEndpoint = context === 'admin'
    ? '/v1/admin/companyNoteSettings/save'
    : '/v1/user/noteSettings/save';

  const rulesRef = useRef(rules);
  const debounceTimer = useRef(null);
  const clearStatusTimer = useRef(null);

  useEffect(() => {
    rulesRef.current = rules;
  }, [rules]);

  useEffect(() => {
    if (selectedProfession === null) return;

    let cancelled = false;

    setRules(emptyRules());
    setFocusTarget(null);
    setSaveStatus('idle');
    clearTimeout(debounceTimer.current);
    clearTimeout(clearStatusTimer.current);

    apiClient.JMPost(getEndpoint, { profession: selectedProfession }).then((data) => {
      if (cancelled) return;
      if (data && data.rules) {
        setRules(Object.fromEntries(
          CATEGORIES.map((c) => [c.key, data.rules[c.key] || []])
        ));
      }
    }).catch(() => {});

    return () => {
      cancelled = true;
      clearTimeout(debounceTimer.current);
      clearTimeout(clearStatusTimer.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, selectedProfession]);

  const executeSave = () => {
    clearTimeout(debounceTimer.current);
    clearTimeout(clearStatusTimer.current);
    setSaveStatus('saving');
    apiClient.JMPost(saveEndpoint, { profession: selectedProfession, rules: rulesRef.current })
      .then(() => {
        setSaveStatus('saved');
        clearStatusTimer.current = setTimeout(() => setSaveStatus('idle'), 2500);
      })
      .catch(() => {
        setSaveStatus('error');
        clearStatusTimer.current = setTimeout(() => setSaveStatus('idle'), 4000);
      });
  };

  const scheduleSave = () => {
    clearTimeout(debounceTimer.current);
    clearTimeout(clearStatusTimer.current);
    setSaveStatus('idle');
    debounceTimer.current = setTimeout(executeSave, AUTOSAVE_DELAY_MS);
  };

  const handleAddRule = (categoryKey) => {
    setRules((prev) => {
      const current = prev[categoryKey] || [];
      if (current.length >= MAX_RULES) return prev;
      setFocusTarget({ categoryKey, index: current.length });
      const next = { ...prev, [categoryKey]: [...current, ''] };
      rulesRef.current = next;
      return next;
    });
    scheduleSave();
  };

  const handleChangeRule = (categoryKey, index, value) => {
    setRules((prev) => {
      const updated = [...(prev[categoryKey] || [])];
      updated[index] = value;
      const next = { ...prev, [categoryKey]: updated };
      rulesRef.current = next;
      return next;
    });
    scheduleSave();
  };

  const handleRemoveRule = (categoryKey, index) => {
    setRules((prev) => {
      const next = {
        ...prev,
        [categoryKey]: (prev[categoryKey] || []).filter((_, i) => i !== index),
      };
      rulesRef.current = next;
      return next;
    });
    scheduleSave();
  };

  const selectedProfLabel = PROFESSIONS.find((p) => p.key === selectedProfession)?.label || '';

  return (
    <div style={{ padding: '0 4px' }}>
      <div style={{ marginBottom: '16px' }}>
        <h4 style={{ marginBottom: '4px' }}>{title}</h4>
        <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>{description}</p>
      </div>

      {/* Profession selector — admin only */}
      {context === 'admin' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
          <span style={{ fontSize: '13px', fontWeight: '500', color: '#555', whiteSpace: 'nowrap' }}>
            Profession
          </span>
          <select
            value={selectedProfession}
            onChange={(e) => setSelectedProfession(e.target.value)}
            style={{
              fontSize: '13px',
              border: '1px solid #ccc',
              borderRadius: '6px',
              padding: '6px 10px',
              background: '#fff',
              color: '#222',
              cursor: 'pointer',
              minWidth: '220px',
            }}
          >
            {PROFESSIONS.map((p) => (
              <option key={p.key} value={p.key}>{p.label}</option>
            ))}
          </select>
        </div>
      )}

      {/* Scope banner — admin only */}
      {context === 'admin' && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: '#f4faf7',
            border: '1px solid #d5ede4',
            borderRadius: '6px',
            padding: '8px 12px',
            marginBottom: '20px',
            fontSize: '13px',
            color: '#555',
          }}
        >
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#1D9E75', flexShrink: 0, display: 'inline-block' }} />
          {selectedProfession === ALL_PROFESSIONS_KEY ? (
            <span>Showing rules for <strong>All Professions</strong> — these apply to every clinician in your organization.</span>
          ) : (
            <span>Showing rules for <strong>{selectedProfLabel}</strong> — these apply only to {selectedProfLabel} clinicians in your organization.</span>
          )}
        </div>
      )}

      {CATEGORIES.map((category) => {
        const categoryRules = rules[category.key] || [];
        const atMax = categoryRules.length >= MAX_RULES;

        return (
          <div
            key={category.key}
            style={{
              marginBottom: '28px',
              borderBottom: '1px solid #eee',
              paddingBottom: '20px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <strong style={{ fontSize: '15px' }}>{category.label}</strong>
              <TooltipIcon text={category.tooltip} />
              <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#888' }}>
                {categoryRules.length}/{MAX_RULES} rules
              </span>
            </div>

            {categoryRules.length === 0 && (
              <div style={{ color: '#aaa', fontSize: '13px', marginBottom: '10px', fontStyle: 'italic' }}>
                No rules added yet.
              </div>
            )}

            {categoryRules.map((rule, index) => (
              <RuleInput
                key={index}
                rule={rule}
                index={index}
                autoFocus={focusTarget?.categoryKey === category.key && focusTarget?.index === index}
                placeholder={index === 0 ? category.placeholder : ''}
                onChange={(i, val) => handleChangeRule(category.key, i, val)}
                onRemove={(i) => handleRemoveRule(category.key, i)}
                onSave={executeSave}
                saveText={STATUS_LABEL[saveStatus] ?? 'Save'}
              />
            ))}

            <Button
              size="sm"
              color="success"
              outline
              disabled={atMax}
              onClick={() => handleAddRule(category.key)}
              style={{ marginTop: '4px' }}
            >
              + Add Rule
              {atMax && <span style={{ marginLeft: '6px', color: '#888' }}>(max {MAX_RULES})</span>}
            </Button>
          </div>
        );
      })}
    </div>
  );
};

export default CompanyNoteSettings;
